import { useEffect, useState } from "react";
import { Save, MessageCircle, CalendarCheck } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, useApi } from "../lib/api";
import { PageHeader, Loading, DataState, Field, NativeSelect, Badge } from "../components/common";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { fmtDateTime } from "../lib/format";
import ManagedListPanel, { QUALIFICATIONS_CFG, SITE_TYPES_CFG, COST_CATEGORIES_CFG } from "../components/ManagedListPanel";
import DatabaseReset from "../components/DatabaseReset";
import { setWorkSettingsCache } from "../components/ShiftFields";
import { useAuth } from "../lib/auth";

function General() {
  const { data } = useApi("/settings");
  const [f, setF] = useState(null);
  useEffect(() => { if (data) setF(data); }, [data]);
  if (!f) return <Loading />;
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    try {
      const r = await api.put("/settings", { ...f, expiry_warning_days: Number(f.expiry_warning_days) });
      setF(r.data);
      setWorkSettingsCache(r.data);
      toast.success("✓ Impostazioni salvate");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="surface p-5 grid sm:grid-cols-2 gap-4 max-w-3xl">
      <Field label="Ragione sociale"><Input value={f.company_name} onChange={set("company_name")} data-testid="settings-company-input" /></Field>
      <Field label="Orario turno predefinito (giornata intera)"><Input value={f.default_shift} onChange={set("default_shift")} placeholder="07:30-16:30" data-testid="settings-shift-input" /></Field>
      <Field label={`Pausa pranzo (giornata intera = ${Number(f.default_hours).toLocaleString("it-IT")} ore)`}><Input value={f.lunch_break || ""} onChange={set("lunch_break")} placeholder="12:00-13:00" data-testid="settings-lunch-input" /></Field>
      <Field label="Preavviso scadenze (giorni)"><Input type="number" value={f.expiry_warning_days} onChange={set("expiry_warning_days")} data-testid="settings-expiry-input" /></Field>
      <Field label="Provider WhatsApp"><NativeSelect value={f.whatsapp_provider} onChange={set("whatsapp_provider")}><option value="simulated">Simulazione (demo)</option><option value="meta" disabled>WhatsApp Business API Meta (predisposto)</option></NativeSelect></Field>
      <Field label="Provider calendario"><NativeSelect value={f.gcal_provider} onChange={set("gcal_provider")}><option value="simulated">Simulazione (demo)</option><option value="google" disabled>Google Calendar OAuth2 (predisposto)</option></NativeSelect></Field>
      <div className="sm:col-span-2"><Button onClick={save} data-testid="settings-save-btn"><Save className="h-4 w-4 mr-2" />Salva impostazioni</Button></div>
    </div>
  );
}

function Audit() {
  const [action, setAction] = useState("");
  const { data, loading } = useApi("/audit", { action });
  const actions = ["login", "logout", "creazione_operaio", "modifica_operaio", "eliminazione_operaio", "creazione_cantiere", "modifica_cantiere", "creazione_mezzo", "assegnazione", "cancellazione_assegnazione", "copia_pianificazione", "modifica_mansioni", "modifica_tipologie", "dotazione_operaio"];
  return (
    <div className="surface p-4">
      <NativeSelect value={action} onChange={(e) => setAction(e.target.value)} className="sm:w-72 mb-3" data-testid="audit-action-filter">
        <option value="">Tutte le operazioni</option>{actions.map((a) => <option key={a} value={a}>{a.replace(/_/g, " ")}</option>)}
      </NativeSelect>
      <DataState loading={loading && !data} empty={!data?.items?.length} emptyTitle="Nessuna operazione registrata">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[640px]" data-testid="audit-table">
            <thead><tr className="text-left text-xs uppercase text-muted-foreground border-b border-border"><th className="py-2">Data e ora</th><th>Utente</th><th>Operazione</th><th>Dettagli</th></tr></thead>
            <tbody>{(data?.items || []).map((a) => (
              <tr key={a.id} className="border-b border-border/50"><td className="py-2 num text-xs whitespace-nowrap pr-3">{fmtDateTime(a.created_at)}</td><td className="pr-3 text-xs">{a.user_email}</td>
                <td className="pr-3"><Badge className="bg-slate-800 border-slate-600 text-slate-200">{a.action.replace(/_/g, " ")}</Badge></td><td className="text-xs text-muted-foreground">{a.details}</td></tr>
            ))}</tbody>
          </table>
        </div>
      </DataState>
    </div>
  );
}

function History() {
  const [channel, setChannel] = useState("whatsapp");
  const { data, loading } = useApi("/integrations/history", { channel });
  return (
    <div className="surface p-4">
      <div className="flex gap-2 mb-3">
        <Button size="sm" variant={channel === "whatsapp" ? "secondary" : "ghost"} onClick={() => setChannel("whatsapp")} data-testid="history-whatsapp-btn"><MessageCircle className="h-4 w-4 mr-1" />WhatsApp</Button>
        <Button size="sm" variant={channel === "gcal" ? "secondary" : "ghost"} onClick={() => setChannel("gcal")} data-testid="history-gcal-btn"><CalendarCheck className="h-4 w-4 mr-1" />Google Calendar</Button>
      </div>
      <DataState loading={loading && !data} empty={!data?.items?.length} emptyTitle="Nessun messaggio simulato">
        <ul className="divide-y divide-border/60" data-testid="integration-history">
          {(data?.items || []).map((m) => (
            <li key={m.id} className="py-3 text-sm">
              <div className="flex justify-between gap-2"><span className="font-medium">{m.recipient} <span className="text-xs text-muted-foreground num">{m.phone}</span></span><span className="text-xs text-muted-foreground">{fmtDateTime(m.created_at)}</span></div>
              <div className="text-slate-300 mt-0.5">{m.message}</div>
              <div className="text-xs text-emerald-400 mt-0.5">✓ {m.status}</div>
            </li>
          ))}
        </ul>
      </DataState>
    </div>
  );
}

export default function SettingsPage() {
  const { user } = useAuth();
  return (
    <div>
      <PageHeader eyebrow="Amministrazione" title="Impostazioni" subtitle="Configurazione, mansioni, tipologie cantiere, registro attività e integrazioni" />
      <Tabs defaultValue="general">
        <TabsList className="mb-4 flex-wrap h-auto">
          <TabsTrigger value="general" data-testid="settings-tab-general">Generali</TabsTrigger>
          <TabsTrigger value="qualifications" data-testid="settings-tab-qualifications">Mansioni</TabsTrigger>
          <TabsTrigger value="site-types" data-testid="settings-tab-site-types">Tipologie cantiere</TabsTrigger>
          <TabsTrigger value="cost-categories" data-testid="settings-tab-cost-categories">Categorie costi</TabsTrigger>
          <TabsTrigger value="audit" data-testid="settings-tab-audit">Audit log</TabsTrigger>
          <TabsTrigger value="history" data-testid="settings-tab-history">Cronologia integrazioni</TabsTrigger>
          {user.can_import && <TabsTrigger value="database" data-testid="settings-tab-database">Database</TabsTrigger>}
        </TabsList>
        <TabsContent value="general"><General /></TabsContent>
        <TabsContent value="qualifications"><ManagedListPanel cfg={QUALIFICATIONS_CFG} /></TabsContent>
        <TabsContent value="site-types"><ManagedListPanel cfg={SITE_TYPES_CFG} /></TabsContent>
        <TabsContent value="cost-categories"><ManagedListPanel cfg={COST_CATEGORIES_CFG} /></TabsContent>
        <TabsContent value="audit"><Audit /></TabsContent>
        <TabsContent value="history"><History /></TabsContent>
        {user.can_import && <TabsContent value="database"><DatabaseReset /></TabsContent>}
      </Tabs>
    </div>
  );
}
